import { NodeLayout } from '@/components/nodes/layout';
import { Button } from '@/components/ui/button';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useI18n } from '@/providers/i18n';
import { useReactFlow, useNodeConnections } from '@xyflow/react';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { areNodePropsEqual } from '../are-node-props-equal';
import { useAutoGenerateHandler } from '@/components/canvas/utils/auto-generate-bus';
import type { ComponentProps } from 'react';
import { Loader2Icon, PlayIcon } from 'lucide-react';
import type { ToolDataShape, ToolDefinition } from './types';
import { relightTool } from './relight/index';
import { poseTool } from './pose';
import { cameraTool } from './camera';
import { lutTool } from './lut';
import { ToolChooser } from './chooser';
import { ToolCollapsedSummary } from './collapsed-summary';
import { nanoid } from 'nanoid';
import { getAllImagesFromNodes } from '@/lib/xyflow';
import { getIncomers } from '@xyflow/react';
import { useProject } from '@/providers/project';
import { useAuth, useUser } from '@clerk/nextjs';

export type ToolNodeProps = {
    type: string;
    data: ToolDataShape;
    id: string;
};

const TOOLS: Record<string, ToolDefinition<any>> = {
    relight: relightTool,
    pose: poseTool,
    camera: cameraTool,
    lut: lutTool,
};

export const ToolNode = memo(({ data, id, type }: ToolNodeProps) => {
    const { t } = useI18n();
    const { updateNodeData, setNodes, addNodes, addEdges, getNode, getNodes, getEdges } = useReactFlow();
    const project = useProject();
    const { getToken } = useAuth();
    const { user } = useUser();
    const [isRunning, setIsRunning] = useState(false);

    const rawToolId = (data?.tool?.id as string | undefined) ?? undefined;
    const toolId = rawToolId && TOOLS[rawToolId] ? rawToolId : undefined;
    const toolDef = (toolId ? TOOLS[toolId] : undefined) as ToolDefinition<any> | undefined;
    const rawParams = (data?.tool?.params as Record<string, unknown> | undefined) ?? {};
    const normalized = useMemo(() => (toolDef ? toolDef.normalize(rawParams) : { params: {}, needsHydrate: false }), [toolDef, rawParams]);

    // Perf: only re-run when the upstream connections set actually changes
    // (instead of every full graph mutation via getNodes/getEdges).
    const incomingConnections = useNodeConnections({ id, handleType: 'target' });
    const incomingSourceIds = useMemo(
        () =>
            (incomingConnections || [])
                .map((c: any) => String(c?.source ?? ''))
                .filter(Boolean),
        [incomingConnections],
    );
    // Tool-specific run gating (e.g. DaVinci LUT / Relight require an image input).
    // Re-evaluates only when the toolId, local params, or upstream connection
    // sources change — not on every unrelated node update.
    const canRun = useMemo(() => {
        try {
            if (!toolId) return false;
            if (toolId !== 'lut' && toolId !== 'relight' && toolId !== 'camera') return true;
            const hasOwn = typeof (normalized.params as any)?.source_image_url === 'string' && String((normalized.params as any).source_image_url).trim();
            if (hasOwn) return true;
            // Allow running if there's an upstream image connected to this tool node.
            // We still call getNodes/getEdges to read the latest snapshot, but the
            // memo only invalidates when incomingSourceIds changes.
            const nodesAll = getNodes();
            const edgesAll = getEdges();
            const incomers = getIncomers({ id }, nodesAll as any, edgesAll as any);
            const images = getAllImagesFromNodes(incomers as any, edgesAll as any, id);
            return images.some((img: any) => typeof img?.url === 'string' && String(img.url).trim());
        } catch {
            // Fail-safe: if a tool requires image input, keep Run disabled on error
            if (!toolId) return false;
            return toolId !== 'lut' && toolId !== 'relight' && toolId !== 'camera';
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [toolId, normalized.params, incomingSourceIds, id]);

    // Tools that depend on an upstream image: keep tool.params.source_image_url synced to the first upstream image URL.
    // Logic moved to components/canvas.tsx (Centralized Tool Node Input Sync) to improve performance.


    // Ensure defaults exist in node.data (so downstream consumers can rely on stable shape)
    useEffect(() => {
        try {
            if (!data?.tool?.id) return;
            // Normalize / migrate params and write back if needed
            if (normalized.needsHydrate) {
                updateNodeData(id, { tool: { id: (toolDef?.id ?? data?.tool?.id) as any, params: normalized.params } });
            }
        } catch { }
    }, [data?.tool?.id, id, normalized.needsHydrate, normalized.params, toolDef, updateNodeData]);

    const patchTool = (nextToolId: string, nextParams?: Record<string, unknown>) => {
        const def = (TOOLS[nextToolId] ?? TOOLS.relight) as ToolDefinition<any>;
        updateNodeData(id, {
            tool: {
                id: def.id,
                params: nextParams ?? { ...def.defaultParams },
            },
        });
    };

    const patchParams = (patch: Record<string, unknown>) => {
        if (!toolDef || !toolId) return;
        updateNodeData(id, { tool: { id: toolDef.id, params: { ...(normalized.params ?? {}), ...patch } } });
    };

    const handleRun = useCallback(() => {
        void (async () => {
            if (isRunning) return;
            if (!canRun) return;
            setIsRunning(true);
            try {
                try {
                    // Prevent undoing past a "run" action (same pattern as GenerateItem)
                    window.dispatchEvent(new Event('flowmuse:history-barrier'));
                } catch { }

                const now = new Date().toISOString();
                const params = (normalized.params ?? {}) as Record<string, unknown>;

                // Capture upstream image input URL from incoming edges to this tool node
                let upstreamImageUrl: string | undefined;
                try {
                    const nodesAll = getNodes();
                    const edgesAll = getEdges();
                    const incomers = getIncomers({ id }, nodesAll as any, edgesAll as any);
                    const images = getAllImagesFromNodes(incomers as any, edgesAll as any, id);
                    for (const img of images) {
                        const url = (img as any)?.url;
                        if (typeof url === 'string' && url.trim()) {
                            upstreamImageUrl = url.trim();
                            break;
                        }
                    }
                } catch { }

                const hasOwnSource = (() => {
                    const v = (params as any)?.source_image_url;
                    return typeof v === 'string' && v.trim().length > 0;
                })();
                const paramsWithInputs: Record<string, unknown> =
                    upstreamImageUrl && !hasOwnSource ? { ...params, source_image_url: upstreamImageUrl } : params;

                // Always persist tool run metadata on the tool node itself
                updateNodeData(id, {
                    updatedAt: now,
                    tool: { id: toolId, params: paramsWithInputs, last_run_at: now },
                });

                const auth = await (async () => {
                    try {
                        const token = await getToken({ template: 'supabase' });
                        const userId = user?.id ? String(user.id) : '';
                        const projectId = project?.id ? String(project.id) : undefined;
                        if (!token || !userId) return undefined;
                        return { token, userId, projectId };
                    } catch {
                        return undefined;
                    }
                })();

                if (!toolDef || !toolId) return;
                const action = await toolDef.onRun?.({
                    params: paramsWithInputs as any,
                    now,
                    sourceNodeId: id,
                    auth,
                } as any);
                if (!action?.createNode && !action?.createGraph) return;

                const source = getNode(id) as any;
                const width = (source?.measured?.width ?? 320) as number;
                const baseX = (source?.position?.x ?? 0) as number;
                const baseY = (source?.position?.y ?? 0) as number;

                // Multi-node graph creation (e.g., Pose workflow)
                if (action?.createGraph?.nodes?.length) {
                    const graph = action.createGraph;
                    // Default: place spawned nodes just to the right of this tool node (similar to createNode)
                    const nearX = baseX + width + 140;
                    // Pose-like workflow: use two columns + extra spacing for the fan-in layout
                    const col1X = nearX;
                    const col2X = col1X + 560;
                    const yGap = 220;

                    const nodesSpec = graph.nodes;
                    const idByKey = new Map<string, string>();
                    for (const n of nodesSpec) idByKey.set(n.key, nanoid());

                    const hasPoseFanIn =
                        nodesSpec.length >= 3 &&
                        nodesSpec.some((n) => n.key === 'upload') &&
                        nodesSpec.some((n) => n.key === 'pose') &&
                        nodesSpec.some((n) => n.key === 'image');

                    // Simple layout heuristic:
                    // - Pose workflow: (upload/pose) on left column, (image) on right column
                    // - Otherwise: keep things close to the tool node (nearX)
                    const positionForKey = (key: string) => {
                        // If graph is a single node (common for tool -> image), keep it tight.
                        if (nodesSpec.length === 1) return { x: nearX, y: baseY };

                        if (hasPoseFanIn) {
                            if (key === 'image') return { x: col2X, y: baseY };
                            if (key === 'upload') return { x: col1X, y: baseY - yGap };
                            if (key === 'pose') return { x: col1X, y: baseY + yGap };
                        }

                        // Default fallback: stack near the tool node.
                        const idx = Math.max(0, nodesSpec.findIndex((n) => n.key === key));
                        return { x: nearX, y: baseY + (idx - 1) * 180 };
                    };

                    addNodes(
                        nodesSpec.map((n) => ({
                            id: idByKey.get(n.key)!,
                            type: n.type,
                            position: positionForKey(n.key),
                            data: n.data,
                            origin: [0, 0.5],
                            selected: false,
                        })) as any
                    );

                    const selectKey = graph.selectKey ?? graph.autoRunKey ?? nodesSpec[nodesSpec.length - 1]?.key;
                    const selectId = selectKey ? idByKey.get(selectKey) : undefined;
                    if (selectId) {
                        setTimeout(() => {
                            try {
                                setNodes((prev: any[]) =>
                                    prev.map((n) => ({ ...n, selected: String(n.id) === String(selectId) }))
                                );
                            } catch { }
                        }, 0);
                    }

                    if (Array.isArray(graph.edges) && graph.edges.length) {
                        try {
                            addEdges(
                                graph.edges.map((e) => ({
                                    id: nanoid(),
                                    source: idByKey.get(e.sourceKey)!,
                                    target: idByKey.get(e.targetKey)!,
                                    type: e.type ?? 'animated',
                                })) as any
                            );
                        } catch { }
                    }

                    // Optionally connect this tool node -> a node in the created graph
                    // (useful for tool nodes that spawn a single downstream node but still use createGraph)
                    if (action.connectFromSource) {
                        const connectKey =
                            graph.selectKey ??
                            graph.autoRunKey ??
                            nodesSpec[nodesSpec.length - 1]?.key;
                        const connectId = connectKey ? idByKey.get(connectKey) : undefined;
                        if (connectId) {
                            try {
                                addEdges({
                                    id: nanoid(),
                                    source: id,
                                    target: connectId,
                                    type: 'animated',
                                } as any);
                            } catch { }
                        }
                    }

                    const autoKey = graph.autoRunKey;
                    const autoId = autoKey ? idByKey.get(autoKey) : undefined;
                    if (autoId) {
                        try {
                            setTimeout(() => {
                                try {
                                    window.dispatchEvent(new CustomEvent('auto_generate_node', { detail: { id: autoId } }));
                                } catch { }
                            }, 30);
                        } catch { }
                    }
                    return;
                }

                if (!action?.createNode) return;

                const newId = nanoid();
                const position = { x: baseX + width + 140, y: baseY };

                addNodes({
                    id: newId,
                    type: action.createNode.type,
                    position,
                    data: action.createNode.data,
                    origin: [0, 0.5],
                    selected: true,
                } as any);

                // Select only the new node (avoid multi-select)
                setTimeout(() => {
                    try {
                        setNodes((prev: any[]) =>
                            prev.map((n) => ({ ...n, selected: String(n.id) === String(newId) }))
                        );
                    } catch { }
                }, 0);

                if (action.connectFromSource) {
                    try {
                        addEdges({
                            id: nanoid(),
                            source: id,
                            target: newId,
                            type: 'animated',
                        } as any);
                    } catch { }
                }

                if (action.autoRun) {
                    try {
                        setTimeout(() => {
                            try {
                                window.dispatchEvent(new CustomEvent('auto_generate_node', { detail: { id: newId } }));
                            } catch { }
                        }, 30);
                    } catch { }
                }
            } finally {
                setIsRunning(false);
            }
        })();
    }, [
        addEdges,
        addNodes,
        canRun,
        getEdges,
        getNodes,
        getNode,
        getToken,
        id,
        isRunning,
        normalized.params,
        project?.id,
        setNodes,
        toolDef,
        toolId,
        updateNodeData,
        user?.id,
    ]);

    // Allow external triggers (node sidebar / auto-run) to "run" this tool node
    // (uses central auto_generate_node router — see auto-generate-bus.ts).
    useAutoGenerateHandler(
        id,
        useCallback(() => {
            try {
                handleRun();
            } catch {}
        }, [handleRun]),
    );

    const toolbar: ComponentProps<typeof NodeLayout>['toolbar'] = [
        {
            tooltip: t('Tool'),
            children: (
                <div className="mr-2 shrink-0">
                    <Select
                        value={toolId}
                        onValueChange={(v) => {
                            patchTool(v);
                        }}
                    >
                        <SelectTrigger size="sm" className="h-7 w-[180px] rounded-full">
                            <span className="flex flex-1 items-center justify-center text-center">
                                <SelectValue placeholder={t('Tool')} />
                            </span>
                        </SelectTrigger>
                        <SelectContent>
                            {Object.values(TOOLS).map((def) => (
                                <SelectItem key={def.id} value={def.id}>
                                    {t(def.label)}
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            ),
        },
        {
            tooltip: t('Run'),
            children: (
                <Button
                    size="icon"
                    className="rounded-full shrink-0"
                    disabled={isRunning || !canRun}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleRun();
                    }}
                    aria-label={
                        !canRun ? t('Please input image') : isRunning ? t('Running...') : t('Run')
                    }
                    title={
                        !canRun ? t('Please input image') : isRunning ? t('Running...') : t('Run')
                    }
                >
                    {isRunning ? (
                        <Loader2Icon size={12} className="animate-spin" />
                    ) : (
                        <PlayIcon size={12} />
                    )}
                </Button>
            ),
        },
    ];

    // Tool-specific aspect ratios
    const nodeAspectRatio: ComponentProps<typeof NodeLayout>['aspectRatio'] =
        !toolId
            ? '2/3'
            : toolId === 'pose'
                ? '1/1'
                : toolId === 'relight'
                    ? '1/1'
                    : toolId === 'lut'
                        ? '1/1'
                        : toolId === 'camera'
                            ? '2/3'
                            : undefined;

    return (
        <NodeLayout
            id={id}
            data={data as any}
            title={t('Tool Node')}
            type={type}
            variant="modern"
            toolbar={toolId ? toolbar : undefined}
            aspectRatio={nodeAspectRatio}
            collapsedAspectRatio="16/9"
            collapsedSummary={<ToolCollapsedSummary data={data as any} />}
        >
            {toolId && toolDef ? (
                <div className={`flex flex-col gap-4 p-3 ${(toolId === 'pose' || toolId === 'lut' || toolId === 'camera' || toolId === 'relight') ? 'h-full' : ''}`}>
                    {toolDef.Panel({
                        params: normalized.params,
                        onChange: (patch) => patchParams(patch as unknown as Record<string, unknown>),
                        nodeId: id,
                    })}
                </div>
            ) : (
                <ToolChooser
                    onSelect={(next) => {
                        patchTool(next);
                    }}
                />
            )}
        </NodeLayout>
    );
}, areNodePropsEqual);
ToolNode.displayName = 'ToolNode';

